// CYBORG-689 — classify a message-search hit into the source type the two
// search surfaces (MessageSearch.svelte + MobileSearchOverlay.svelte) render,
// and the deep-link path a click navigates to. Pure (no runes/state imports) so
// it is unit-testable under plain-node vitest — same split reason as
// message-link-path.ts / cybo-dm-peer-id.ts.
//
// The deep-link vocabulary is the SAME `?m=<messageId>` param message-link-path.ts
// mints and the channel/[channelId] + dm/[userId] routes already consume
// (messageFocusState.focus() → scroll + flash the exact row). This resolver adds
// the two things message-link-path.ts intentionally does NOT do for a permalink:
//   • distinguishes public vs private vs group-DM channels (the glyph), and
//   • routes an agent/cybo DM to /agent/<id> (ChannelSidebar's routing truth),
//     not /dm/<id> — a cybo is not a workspace member, so /dm/<cyboId> is empty.

import type { Message } from "./core/types.js";
import { isMe, type ViewerIdentity } from "./session-scope.js";
import { messageDeepLink } from "./message-link-path.js";

export type SearchResultKind = "public" | "private" | "group_dm" | "dm";

export interface SearchResultView {
  /** Source type — drives the glyph (hash / lock / people / avatar). */
  kind: SearchResultKind;
  /** Display label: channel name, group-DM name, or the DM peer's name. */
  name: string;
  /** DM peer id (human userId or agent/cybo id); null for channel hits. */
  peerId: string | null;
  /** True when a DM peer is an agent/cybo → routes /agent/, avatar+name glyph. */
  isAgentPeer: boolean;
  /** Deep-link path (with `?m=`) the click navigates to, or null if unresolvable. */
  href: string | null;
}

/** Minimal channel shape this resolver reads — a subset of core `Channel`. */
export interface SearchResultChannel {
  name?: string;
  isPrivate?: boolean;
  type?: "regular" | "group_dm";
  isHidden?: boolean;
}

export interface SearchResultContext {
  /** message.workspaceId ?? the active workspace id — resolved by the caller. */
  workspaceId: string | undefined;
  /**
   * The viewer, to resolve the DM counterparty from fromId/toId. Pass the user
   * OBJECT (`authState.user`), not an id: a human holds TWO ids (session-scope.ts
   * `ViewerIdentity`) and only the object carries the second one. A bare id still
   * type-checks and degrades to the old single-id compare.
   */
  viewer: ViewerIdentity;
  /** Channel lookup by id (the viewer's channel list). Undefined = not in list. */
  getChannel: (channelId: string) => SearchResultChannel | undefined;
  /** Peer display-name resolver (human member OR cybo), by id. */
  getPeerName: (peerId: string) => string | undefined;
  /** Is this id an agent/cybo (routes to /agent, avatar glyph)? */
  isAgentPeer: (peerId: string) => boolean;
  /**
   * Is this id a known HUMAN workspace member? Optional — a caller without a
   * member roster (the unit tests) omits it. When supplied it lets classifyDmHit
   * derive agent-ness for a DM I authored to a cybo that has no live session and
   * isn't in my cybo roster: a 1:1 DM peer is only ever a human member or a cybo,
   * so a non-member peer (that isn't me) is a cybo. See classifyDmHit.
   */
  isHumanMember?: (peerId: string) => boolean;
}

export function classifySearchResult(m: Message, ctx: SearchResultContext): SearchResultView {
  const deepLink = messageDeepLink(m.id, m.parentId);
  const wsPrefix = ctx.workspaceId ? `/workspace/${ctx.workspaceId}` : null;
  if (m.channelId) return classifyChannelHit(m.channelId, ctx, wsPrefix, deepLink);
  return classifyDmHit(m, ctx, wsPrefix, deepLink);
}

function classifyChannelHit(
  channelId: string,
  ctx: SearchResultContext,
  wsPrefix: string | null,
  deepLink: string,
): SearchResultView {
  const ch = ctx.getChannel(channelId);
  // A group DM is a hidden group_dm channel (#608): mirror QuickSwitcher's
  // derivation. Everything else splits on isPrivate. A public channel the viewer
  // hasn't joined is absent from getChannel — the server never returns
  // private-channel hits the viewer can't see, so an unknown channel is treated
  // as public (hash), never leaking a lock/name it can't resolve.
  let kind: SearchResultKind = "public";
  if (ch?.type === "group_dm" || ch?.isHidden === true) kind = "group_dm";
  else if (ch?.isPrivate === true) kind = "private";
  return {
    kind,
    name: ch?.name ?? "channel",
    peerId: null,
    isAgentPeer: false,
    href: wsPrefix ? `${wsPrefix}/channel/${channelId}${deepLink}` : null,
  };
}

function classifyDmHit(
  m: Message,
  ctx: SearchResultContext,
  wsPrefix: string | null,
  deepLink: string,
): SearchResultView {
  // DM (channelId === null): the peer is whichever party isn't the viewer — same call
  // as message-link-path.ts / app.svelte.ts's dmCounterparty. Through `isMe`, never
  // `fromId === myId`: my own hit arrives stamped with EITHER of my two ids, so a bare
  // compare called it someone else's and routed the result to a DM with MYSELF.
  let peerId: string | null = null;
  if (ctx.viewer) {
    peerId = isMe(m.fromId, ctx.viewer) ? (m.toId ?? null) : m.fromId;
  }
  const authoredByPeer = peerId != null && m.fromId === peerId;
  // A DM peer is an agent/cybo (→ /agent, avatar glyph) when ANY of:
  //   1. the message is agent-authored BY the peer (fromType "agent" + peer sent
  //      it) — the only signal on a peer-authored hit,
  //   2. the peer resolves as an agent via ctx.isAgentPeer — the SAME roster /
  //      dmSessionByCybo(cyboId) lookup the /agent route uses, or
  //   3. DERIVED: the peer is neither me nor a known HUMAN member. A 1:1 DM peer
  //      is only ever a human member or a cybo, so a peer absent from the member
  //      roster is a cybo. This is what fixes a DM *I* authored to a cybo that is
  //      NOT in my local cybo roster and has no live session: my own message is
  //      fromType "human" (clause 1 can't fire) and the roster miss defeats
  //      clause 2, so without this it wrongly routed to /dm/<cyboId> (empty — a
  //      cybo is not a workspace member).
  //
  // RESIDUAL (documented, not derivable): a former human member who LEFT the
  // workspace is also absent from the roster, so a DM to them derives as an agent
  // and routes to /agent instead of /dm. Rare, and the peer is unreachable either
  // way. `isHumanMember` is optional, so a caller with no roster (unit tests)
  // simply skips clause 3 and keeps the roster-only behavior.
  const isAgentPeer =
    (m.fromType === "agent" && authoredByPeer) ||
    (peerId != null && ctx.isAgentPeer(peerId)) ||
    // `isMe`, not `peerId !== currentUserId`: a self-DM whose peer field carries my
    // OTHER id is still me, and a bare compare made it a non-member → derived it as a
    // cybo → routed my own self-DM to /agent/<my own id>.
    (peerId != null &&
      !isMe(peerId, ctx.viewer) &&
      ctx.isHumanMember != null &&
      !ctx.isHumanMember(peerId));
  // Prefer the resolved peer name; fall back to the author name only when the
  // peer authored the message; else a neutral label.
  const name =
    (peerId ? ctx.getPeerName(peerId) : undefined) ??
    (authoredByPeer ? m.fromName : undefined) ??
    "Direct message";
  const route = isAgentPeer ? "agent" : "dm";
  return {
    kind: "dm",
    name,
    peerId,
    isAgentPeer,
    href: wsPrefix && peerId ? `${wsPrefix}/${route}/${peerId}${deepLink}` : null,
  };
}
